import { useEffect, useRef, useState } from "react";
import moment from "moment";
import _ from "lodash";
import { toast } from "react-toastify";

import { getAvatarUrl, getImageUrl } from "src/Common/common";
import TechnicianService from "src/Services/API/technician.service";
import { getMinutesFromFormattedHours, handleError } from "src/Common/function";
import { timesheetGridClass, StoredTimeCell, TotalTimeCell } from "./TimesheetTable";
import TimeInput, {
  EMPTY_TIME,
  isNormalizedTimeValue,
  normalizeTimeValue,
  parseUserTime,
} from "src/Components/Common/timeinput";

const DayTechItem = ({
  tech,
  currentDate,
  startDateString,
  timesheetUpdated,
  activeEditingTechId,
  setActiveEditingTechId,
  canEdit,
}) => {
  const [techData, setTechData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Raw strings the user is currently typing. Only normalized on commit.
  const [editedTimes, setEditedTimes] = useState({
    regularTime: EMPTY_TIME,
    travelTime: EMPTY_TIME,
    overTime: EMPTY_TIME,
  });

  // Snapshot of normalized times at the moment editing started. Used as the
  // diff baseline on save so we don't send unchanged fields to the API.
  const baselineTimesRef = useRef({
    regularTime: EMPTY_TIME,
    travelTime: EMPTY_TIME,
    overTime: EMPTY_TIME,
  });

  const isEditing = activeEditingTechId === tech?.id;

  useEffect(() => {
    let technician = _.cloneDeep(tech);
    if (tech?.timesheet) {
      for (let i = 0; i < tech.timesheet.length; i++) {
        const workDate = moment(startDateString).add(Number(tech.timesheet[i].dayDiff || 0), "days");
        const isSameDate = workDate.isSame(currentDate, "day");

        if (isSameDate) {
          technician.regularTime = tech.timesheet[i].regularTime;
          technician.travelTime = tech.timesheet[i].travelTime;
          technician.overTime = tech.timesheet[i].overTime;
        }
      }
    }

    setTechData(technician);
    const normalized = {
      regularTime: normalizeTimeValue(technician.regularTime),
      travelTime: normalizeTimeValue(technician.travelTime),
      overTime: normalizeTimeValue(technician.overTime),
    };
    setEditedTimes(normalized);
    baselineTimesRef.current = normalized;
  }, [tech, currentDate, startDateString]);

  const handleFieldKeyDown = (fieldName, event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      // Parse all three fields synchronously so we don't race React state.
      const finalTimes = {
        regularTime: editedTimes.regularTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.regularTime),
        travelTime: editedTimes.travelTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.travelTime),
        overTime: editedTimes.overTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.overTime),
      };
      setEditedTimes(finalTimes);
      updateTimesheet(finalTimes);
      setActiveEditingTechId(null);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      // Revert this field to its baseline and exit edit mode.
      setEditedTimes((previous) => ({
        ...previous,
        [fieldName]: baselineTimesRef.current[fieldName],
      }));
      setActiveEditingTechId(null);
    }
  };

  const renderTimeField = (fieldName) => {
    if (!isEditing) {
      return <StoredTimeCell value={techData?.[fieldName]} />;
    }

    return (
      <TimeInput
        value={editedTimes[fieldName]}
        onChange={(next) => setEditedTimes((previous) => ({ ...previous, [fieldName]: next }))}
        onKeyDown={(e) => handleFieldKeyDown(fieldName, e)}
        // Numeric columns are center-aligned, so the box sits on the column
        // center line and its centered text lines up with the other rows.
        className="justify-self-center w-[88px] h-[34px] text-center rounded-[8px] border-[1px] border-borderColor bg-white font-OpenSansRegular text-[14px] text-[#1A2332] outline-none"
      />
    );
  };

  const onEditButtonClick = () => {
    if (!isEditing) {
      if (activeEditingTechId && activeEditingTechId !== tech.id) {
        return;
      }
      // Snapshot the baseline at the moment we enter edit mode.
      baselineTimesRef.current = {
        regularTime: normalizeTimeValue(techData?.regularTime),
        travelTime: normalizeTimeValue(techData?.travelTime),
        overTime: normalizeTimeValue(techData?.overTime),
      };
      setEditedTimes(baselineTimesRef.current);
      setActiveEditingTechId(tech.id);
      return;
    }

    // Normalize all three fields before saving in case the user clicked the
    // check mark without blurring the input first.
    const finalTimes = {
      regularTime: editedTimes.regularTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.regularTime),
      travelTime: editedTimes.travelTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.travelTime),
      overTime: editedTimes.overTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.overTime),
    };
    setEditedTimes(finalTimes);
    updateTimesheet(finalTimes);
    setActiveEditingTechId(null);
  };

  const updateTimesheet = (overrideTimes) => {
    if (isSubmitting) {
      return;
    }
    const finalTimes = overrideTimes || editedTimes;
    const originalTimes = baselineTimesRef.current;

    let changedTimes = {};
    if (finalTimes.regularTime !== originalTimes.regularTime && isNormalizedTimeValue(finalTimes.regularTime)) {
      changedTimes.regularTime = finalTimes.regularTime;
    }
    if (finalTimes.travelTime !== originalTimes.travelTime && isNormalizedTimeValue(finalTimes.travelTime)) {
      changedTimes.travelTime = finalTimes.travelTime;
    }
    if (finalTimes.overTime !== originalTimes.overTime && isNormalizedTimeValue(finalTimes.overTime)) {
      changedTimes.overTime = finalTimes.overTime;
    }

    if (Object.keys(changedTimes).length === 0) {
      return;
    }

    const dayDiff = moment(currentDate).diff(moment(startDateString), "days");

    let timesheet = [];
    if (techData.timesheet && techData.timesheet.length > 0) {
      timesheet = _.cloneDeep(techData.timesheet);
      if (timesheet) {
        let isFound = false;
        for (let i = 0; i < timesheet.length; i++) {
          if (timesheet[i].dayDiff === dayDiff) {
            if (changedTimes.regularTime) {
              timesheet[i].regularTime = changedTimes.regularTime;
            }
            if (changedTimes.travelTime) {
              timesheet[i].travelTime = changedTimes.travelTime;
            }
            if (changedTimes.overTime) {
              timesheet[i].overTime = changedTimes.overTime;
            }
            isFound = true;
          }
        }
        if (!isFound) {
          let temp = _.cloneDeep(changedTimes);
          temp.dayDiff = dayDiff;
          timesheet.push(temp);
        }
      }
    } else {
      let temp = _.cloneDeep(changedTimes);
      temp.dayDiff = dayDiff;
      timesheet.push(temp);
    }

    setIsSubmitting(true);
    TechnicianService.updateTimesheet({
      id: techData.id,
      data: { timesheet: JSON.stringify(timesheet) },
    })
      .then((response) => {
        toast("Timesheet updated successfully");
        timesheetUpdated();
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => setIsSubmitting(false));
  };

  // While a row is being edited the total keeps showing the last saved values;
  // it refreshes when the save round-trips through timesheetUpdated.
  const dayTotalMinutes =
    getMinutesFromFormattedHours(techData?.regularTime) +
    getMinutesFromFormattedHours(techData?.travelTime) +
    getMinutesFromFormattedHours(techData?.overTime);

  return (
    techData && (
      <div className={`${timesheetGridClass(canEdit)} h-[72px] border-b border-[#E5E8F3] last:border-b-0`}>
        <div className="flex items-center gap-3 min-w-0">
          <img src={getAvatarUrl(techData.user.avatar)} className="w-6 h-6 rounded-full object-cover shrink-0" />
          <p className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477] truncate">
            {techData.user.name}
          </p>
        </div>
        {renderTimeField("regularTime")}
        {renderTimeField("travelTime")}
        {renderTimeField("overTime")}
        <TotalTimeCell minutes={dayTotalMinutes} />
        {canEdit && (
          <EditButton
            clicked={onEditButtonClick}
            isEditing={isEditing}
            isDisabled={!isEditing && activeEditingTechId !== null && activeEditingTechId !== tech.id}
          />
        )}
      </div>
    )
  );
};

export default DayTechItem;

const EditButton = ({ clicked, isEditing = false, isDisabled = false }) => {
  // When editing, swallow mousedown so the focused input doesn't blur before
  // our click handler runs. Blur would fire commitField and race with the
  // save path, leaving us with stale state.
  const handleMouseDown = (event) => {
    if (isEditing) {
      event.preventDefault();
    }
  };

  return isEditing ? (
    <div
      onMouseDown={handleMouseDown}
      onClick={clicked}
      className="w-[18px] justify-self-end cursor-pointer"
    >
      <img src={getImageUrl("/images/common/check.png")} className="w-[18px]" />
    </div>
  ) : (
    <div
      onClick={isDisabled ? undefined : clicked}
      className={`w-[18px] justify-self-end ${isDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
    >
      <img src={getImageUrl("/images/common/blueedit.png")} className="w-[18px]" />
    </div>
  );
};
